import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import PageContainer from '../components/PageContainer';
import { API_BASE_URL } from '../config/api';
import {
  Settings,
  Users,
  MessageCircle,
  Zap,
  ChevronDown,
  ChevronRight,
  UserPlus,
  Briefcase,
  Lock,
  X,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronsLeft,
  ChevronsRight,
} from 'react-feather';
import { UserContext } from '../UserContext';
import '../styles/account.css';
import '../styles/admin.css';

const ALLOWED_EVENT_TYPES = [
  'signup.completed',
  'auth.login.succeeded',
  'conversation.completed',
  'subscription.updated',
];

const EMPTY_WEBHOOK_FORM = {
  event_type: 'signup.completed',
  target_url: '',
  secret: '',
  is_active: true,
  timeout_seconds: 8,
  max_retries: 3,
  retry_backoff_base: 2,
};

const EMPTY_NEW_USER_FORM = {
  email: '',
  password: '',
  contact_name: '',
  contact_phone: '',
  organization_mode: 'existing', // 'existing' | 'new'  (platform admin only)
  organization_id: '',
  organization_name: '',
  organization_url: '',         // only used when creating a new org
  organization_role: 'ADMIN',
  is_platform_admin: false,
};

// Password complexity rules for admin-created users, mirrors the secure
// password requirements expected at signup (one uppercase, one lowercase,
// one number, and one special character, minimum 8 characters).
const PASSWORD_REQUIREMENTS = [
  { test: (pw) => pw.length >= 8, label: 'at least 8 characters' },
  { test: (pw) => /[A-Z]/.test(pw), label: 'one uppercase letter' },
  { test: (pw) => /[a-z]/.test(pw), label: 'one lowercase letter' },
  { test: (pw) => /[0-9]/.test(pw), label: 'one number' },
  { test: (pw) => /[^A-Za-z0-9]/.test(pw), label: 'one special character' },
];

const getPasswordError = (password) => {
  const unmet = PASSWORD_REQUIREMENTS.filter((rule) => !rule.test(password || ''));
  if (unmet.length === 0) return '';
  return `Password must contain ${unmet.map((rule) => rule.label).join(', ')}.`;
};

// Presentation lives in admin.css. This page previously carried three style
// objects here (infoBoxStyle / errorBoxStyle / successBoxStyle) that were
// spread inline at a dozen call sites, each hardcoding the brand palette as
// hex literals, which is how the page drifted off the token system while
// the rest of the app moved onto it. Their CSS equivalents are
// .admin-info-box and .admin-status-box--error / --success.

const ADMIN_TABS = [
  { key: 'general', label: 'General' },
  { key: 'users', label: 'User Management' },
  { key: 'capabilities', label: 'Capabilities' },
];

const PlatformAdmin = () => {
  const { login, userId, isPlatformAdmin, isOrganizationAdmin, organizationId: adminOrgId } = useContext(UserContext);
  const canAccessAdminPage = isPlatformAdmin || isOrganizationAdmin;
  const canShowPlatformControls = isPlatformAdmin;
  const canShowUserManagementActions = isPlatformAdmin || isOrganizationAdmin;
  const adminPageTitle = isPlatformAdmin ? 'Platform Admin' : 'Admin';
  const adminPageSubtitle = isPlatformAdmin
    ? 'Manage platform-wide settings, messaging, and user access'
    : 'Manage users and roles for your organization';

  const [template, setTemplate] = useState('');
  const [originalTemplate, setOriginalTemplate] = useState('');
  const [welcomeTemplate, setWelcomeTemplate] = useState('');
  const [originalWelcomeTemplate, setOriginalWelcomeTemplate] = useState('');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingWelcome, setSavingWelcome] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [switchingUserId, setSwitchingUserId] = useState(null);
  const [removingUserId, setRemovingUserId] = useState(null);
  const [usersLoading, setUsersLoading] = useState(true);

  const [settingsError, setSettingsError] = useState('');
  const [settingsSuccess, setSettingsSuccess] = useState('');
  const [welcomeError, setWelcomeError] = useState('');
  const [welcomeSuccessMessage, setWelcomeSuccessMessage] = useState('');
  const [usersError, setUsersError] = useState('');
  const [userActionError, setUserActionError] = useState('');
  const [userActionSuccess, setUserActionSuccess] = useState('');
  const [createUserError, setCreateUserError] = useState('');
  const [createUserSuccess, setCreateUserSuccess] = useState('');

  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [deleteAssistants, setDeleteAssistants] = useState(false);
  const [usersExpanded, setUsersExpanded] = useState(false);
  const [createUserExpanded, setCreateUserExpanded] = useState(false);
  const [webhooksExpanded, setWebhooksExpanded] = useState(false);

  // Welcome announcement state
  const emptyUserForm = () => ({
    ...EMPTY_NEW_USER_FORM,
    organization_id: adminOrgId ? String(adminOrgId) : '',
  });
  const [newUserForm, setNewUserForm] = useState(emptyUserForm);

  // Webhook state
  const [webhooks, setWebhooks] = useState([]);
  const [webhooksLoading, setWebhooksLoading] = useState(true);
  const [webhooksError, setWebhooksError] = useState('');
  const [webhookFormSuccess, setWebhookFormSuccess] = useState('');
  const [showWebhookForm, setShowWebhookForm] = useState(false);
  const [savingWebhook, setSavingWebhook] = useState(false);
  const [webhookFormError, setWebhookFormError] = useState('');
  const [editingWebhookId, setEditingWebhookId] = useState(null);
  const [deletingWebhookId, setDeletingWebhookId] = useState(null);
  const [webhookForm, setWebhookForm] = useState({ ...EMPTY_WEBHOOK_FORM });
  // Webhook deletion is irreversible and was previously fired by a single
  // click with no confirmation, unlike user removal on the same page, which
  // has always been gated. This holds the webhook awaiting confirmation.
  const [webhookPendingDelete, setWebhookPendingDelete] = useState(null);

  // Tab state (platform admin only)
  const [activeTab, setActiveTab] = useState('general');

  // Organization list (platform admin only, for user creation dropdown)
  const [organizations, setOrganizations] = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(false);

  // User Billing & Credits (platform admin only, shown inside User Management)
  const [selectedBillingUserId, setSelectedBillingUserId] = useState('');
  const [billingSnapshot, setBillingSnapshot] = useState(null);
  const [billingSnapshotLoading, setBillingSnapshotLoading] = useState(false);
  const [billingSnapshotError, setBillingSnapshotError] = useState('');
  const [creditAmount, setCreditAmount] = useState('');
  const [creditAdjustLoading, setCreditAdjustLoading] = useState(false);
  const [creditAdjustError, setCreditAdjustError] = useState('');
  const [creditAdjustSuccess, setCreditAdjustSuccess] = useState('');
  const [customerCode, setCustomerCode] = useState('');
  const [customerCodeLoading, setCustomerCodeLoading] = useState(false);
  const [customerCodeError, setCustomerCodeError] = useState('');
  const [customerCodeSuccess, setCustomerCodeSuccess] = useState('');
  // The code currently on file, plus when it was linked and last changed,
  // read-only, and distinct from `customerCode`, which is the value being
  // typed to replace it.
  const [currentCustomerCode, setCurrentCustomerCode] = useState(null);
  const [currentCustomerCodeLoading, setCurrentCustomerCodeLoading] = useState(false);
  const [currentCustomerCodeError, setCurrentCustomerCodeError] = useState('');
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncError, setSyncError] = useState('');
  const [syncSuccess, setSyncSuccess] = useState('');
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(false);
  const [paymentMethodsError, setPaymentMethodsError] = useState('');

  // Business agent member access state
  const [businessAgents, setBusinessAgents] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [agentMembers, setAgentMembers] = useState([]);
  const [agentAccessLoading, setAgentAccessLoading] = useState(false);
  const [agentAccessError, setAgentAccessError] = useState('');
  const [agentAccessSuccess, setAgentAccessSuccess] = useState('');
  const [addMemberUserId, setAddMemberUserId] = useState('');
  const [agentAccessExpanded, setAgentAccessExpanded] = useState(false);

  // ── User Management: search / filter / sort / pagination ──
  const [userSearch, setUserSearch] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('');
  const [userOrgFilter, setUserOrgFilter] = useState('');
  const [userSortKey, setUserSortKey] = useState('id');
  const [userSortDir, setUserSortDir] = useState('asc');
  const [userPage, setUserPage] = useState(1);
  const [userPageSize, setUserPageSize] = useState(25);
  // Lazy per-page credits cache: { [userId]: number | 'loading' | 'error' }
  const [pageCredits, setPageCredits] = useState({});

  // Refs
  const userSearchRef = useRef(null);
  const billingPanelRef = useRef(null);
  const fetchedCreditsRef = useRef(new Set());

  useEffect(() => {
    if (!canAccessAdminPage) {
      setLoading(false);
      setUsersLoading(false);
      setWebhooksLoading(false);
      return;
    }

    const loadInitialData = async () => {
      setLoading(true);

      const tasks = [fetchUsers()];

      if (canShowPlatformControls) {
        tasks.push(fetchTemplate(), fetchWelcomeTemplate(), fetchWebhooks(), fetchOrganizations());
      }
      if (isOrganizationAdmin) {
        tasks.push(fetchBusinessAgents());
      }

      await Promise.allSettled(tasks);
      setLoading(false);
    };

    loadInitialData();
  }, [canAccessAdminPage, canShowPlatformControls, isOrganizationAdmin]); // eslint-disable-line

  const fetchTemplate = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/system-settings/prompt-template`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch template');
      }

      const data = await response.json();
      setTemplate(data.template);
      setOriginalTemplate(data.template);
    } catch (err) {
      setSettingsError('Failed to load platform prompt template');
    }
  };

  const fetchWelcomeTemplate = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/system-settings/welcome-announcement`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch welcome template');
      }

      const data = await response.json();
      setWelcomeTemplate(data.template);
      setOriginalWelcomeTemplate(data.template);
    } catch (err) {
      setWelcomeError('Failed to load welcome template');
    }
  };

  const fetchUsers = async () => {
    setUsersLoading(true);
    setUsersError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/system-settings/users`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.detail || 'Failed to fetch users');
      }

      const data = await response.json();
      setUsers(data);
      // Clear the lazy credits cache so re-fetched users get fresh credit data
      fetchedCreditsRef.current = new Set();
      setPageCredits({});
    } catch (err) {
      setUsersError(err.message || 'Failed to fetch users');
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    setOrgsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/system-settings/organizations`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Failed to fetch organizations');
      const data = await response.json();
      setOrganizations(data);
    } catch (err) {
      console.error('Error fetching organizations:', err);
    } finally {
      setOrgsLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSettingsError('');
    setSettingsSuccess('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/system-settings/prompt-template`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ template }),
      });

      if (!response.ok) {
        throw new Error('Failed to update template');
      }

      setOriginalTemplate(template);
      setSettingsSuccess('Platform prompt template updated successfully');
    } catch (err) {
      setSettingsError(err.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveWelcome = async () => {
    setSavingWelcome(true);
    setWelcomeError('');
    setWelcomeSuccessMessage('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/system-settings/welcome-announcement`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ template: welcomeTemplate }),
      });

      if (!response.ok) {
        throw new Error('Failed to update welcome template');
      }

      setOriginalWelcomeTemplate(welcomeTemplate);
      setWelcomeSuccessMessage('Welcome announcement updated successfully');
    } catch (err) {
      setWelcomeError(err.message || 'Failed to save changes');
    } finally {
      setSavingWelcome(false);
    }
  };

  const handleCreateUserChange = (event) => {
    const { name, value, type, checked } = event.target;
    setCreateUserError('');
    setCreateUserSuccess('');
    setNewUserForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleCreateUser = async (event) => {
    event.preventDefault();
    setCreatingUser(true);
    setCreateUserError('');
    setCreateUserSuccess('');

    const trimmedEmail = newUserForm.email.trim();
    const trimmedContactName = newUserForm.contact_name.trim();
    const trimmedContactPhone = newUserForm.contact_phone.trim();
    const trimmedOrganizationName = newUserForm.organization_name.trim();
    const trimmedOrganizationUrl = newUserForm.organization_url.trim();

    const passwordError = getPasswordError(newUserForm.password);
    if (passwordError) {
      setCreateUserError(passwordError);
      setCreatingUser(false);
      return;
    }

    if (
      canShowPlatformControls &&
      newUserForm.organization_mode === 'existing' &&
      !newUserForm.organization_id
    ) {
      setCreateUserError('Select an organization before creating the user.');
      setCreatingUser(false);
      return;
    }

    if (
      canShowPlatformControls &&
      newUserForm.organization_mode === 'new' &&
      !trimmedOrganizationName
    ) {
      setCreateUserError('Enter an organization name before creating the user.');
      setCreatingUser(false);
      return;
    }

    let payload;
    if (canShowPlatformControls) {
      const { organization_mode, organization_id, organization_name, organization_url, ...rest } = newUserForm;
      payload = {
        ...rest,
        email: trimmedEmail,
        contact_name: trimmedContactName,
        contact_phone: trimmedContactPhone,
      };
      if (organization_mode === 'existing' && organization_id) {
        payload.organization_id = parseInt(organization_id, 10);
      } else {
        payload.organization_name = trimmedOrganizationName;
        payload.business_url = trimmedOrganizationUrl || '';
      }
    } else {
      const { organization_mode, organization_id, organization_name, organization_url, ...rest } = newUserForm;
      payload = {
        ...rest,
        email: trimmedEmail,
        contact_name: trimmedContactName,
        contact_phone: trimmedContactPhone,
        is_platform_admin: false,
      };
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/users`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(data?.detail || 'Failed to create user');
      }

      setCreateUserSuccess(
        canShowPlatformControls
          ? `User created successfully. Platform API path: ${data?.platform_path || '/api/users'}`
          : 'Organization user created successfully.'
      );
      setNewUserForm(emptyUserForm());
      await fetchUsers();
    } catch (err) {
      setCreateUserError(err.message || 'Failed to create user');
    } finally {
      setCreatingUser(false);
    }
  };

  const handleSwitchUser = async (user) => {
    setSwitchingUserId(user.id);
    setUsersError('');
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    try {
      const response = await fetch(`${API_BASE_URL}/api/system-settings/switch-user`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ user_id: user.id }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.success) {
        throw new Error(data?.detail || 'Failed to switch user');
      }

      localStorage.removeItem('token');
      localStorage.removeItem('ap_token');
      localStorage.removeItem('projectId');

      login(data.userId, null, data.isPlatformAdmin ?? data.isAdmin, {
        isPlatformAdmin: data.isPlatformAdmin ?? data.isAdmin,
        organizationRole: data.organizationRole ?? null,
        organizationId: data.organizationId ?? null,
      });

      localStorage.setItem('access_token', data.access_token);
      if (data.ap_token) {
        localStorage.setItem('ap_token', data.ap_token);
        localStorage.setItem('projectId', data.ap_project_id);
      }

      window.location.replace('/dashboard');
    } catch (err) {
      if (err.name === 'AbortError') {
        setUsersError('Request timed out. Please try again.');
      } else {
        setUsersError(err.message || 'Failed to switch user');
      }
      setSwitchingUserId(null);
    }
  };

  const removeUser = async () => {
    if (!selectedUser) {
      return;
    }

    setRemoving(true);
    setRemovingUserId(selectedUser.id);
    setUserActionError('');
    setUserActionSuccess('');

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    try {
      const response = await fetch(`${API_BASE_URL}/api/system-settings/remove-user`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: selectedUser.id,
          delete_agents: deleteAssistants,
        }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.success) {
        throw new Error(data?.detail || 'Failed to remove user');
      }

      setUserActionSuccess(data.message || 'User removed successfully');
      setSelectedUser(null);
      setShowConfirmation(false);
      setDeleteAssistants(false);
      await fetchUsers();
    } catch (err) {
      if (err.name === 'AbortError') {
        setUserActionError('Request timed out. Please try again.');
      } else {
        setUserActionError(err.message || 'Failed to remove user');
      }
    } finally {
      setRemoving(false);
      setRemovingUserId(null);
    }
  };

  const handleCancel = () => {
    setTemplate(originalTemplate);
    setSettingsError('');
    setSettingsSuccess('');
  };

  const handleCancelWelcome = () => {
    setWelcomeTemplate(originalWelcomeTemplate);
    setWelcomeError('');
    setWelcomeSuccessMessage('');
  };

  const resetCreateUserForm = () => {
    setNewUserForm(emptyUserForm());
    setCreateUserError('');
    setCreateUserSuccess('');
  };

  const fetchWebhooks = async () => {
    setWebhooksLoading(true);
    setWebhooksError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/webhooks/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to fetch webhooks');
      }
      setWebhooks(await response.json());
    } catch (err) {
      setWebhooksError(err.message);
    } finally {
      setWebhooksLoading(false);
    }
  };

  const handleSaveWebhook = async () => {
    setSavingWebhook(true);
    setWebhookFormError('');
    setWebhookFormSuccess('');
    try {
      const isEdit = Boolean(editingWebhookId);
      const url = isEdit
        ? `${API_BASE_URL}/api/admin/webhooks/${editingWebhookId}`
        : `${API_BASE_URL}/api/admin/webhooks/`;
      const payload = { ...webhookForm };
      if (isEdit && !payload.secret) delete payload.secret;

      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to save webhook');
      }
      setWebhookFormSuccess(isEdit ? 'Webhook updated successfully' : 'Webhook created successfully');
      setShowWebhookForm(false);
      setEditingWebhookId(null);
      setWebhookForm({ ...EMPTY_WEBHOOK_FORM });
      fetchWebhooks();
    } catch (err) {
      setWebhookFormError(err.message);
    } finally {
      setSavingWebhook(false);
    }
  };

  const handleDeleteWebhook = async (webhookId) => {
    setDeletingWebhookId(webhookId);
    setWebhookPendingDelete(null);
    setWebhooksError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/webhooks/${webhookId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to delete webhook');
      }
      fetchWebhooks();
    } catch (err) {
      setWebhooksError(err.message);
    } finally {
      setDeletingWebhookId(null);
    }
  };

  const startEditWebhook = (webhook) => {
    setEditingWebhookId(webhook.id);
    setWebhookForm({
      event_type: webhook.event_type,
      target_url: webhook.target_url,
      secret: '',
      is_active: webhook.is_active,
      timeout_seconds: webhook.timeout_seconds,
      max_retries: webhook.max_retries,
      retry_backoff_base: webhook.retry_backoff_base,
    });
    setWebhookFormError('');
    setWebhookFormSuccess('');
    setShowWebhookForm(true);
  };

  const cancelWebhookForm = () => {
    setShowWebhookForm(false);
    setEditingWebhookId(null);
    setWebhookForm({ ...EMPTY_WEBHOOK_FORM });
    setWebhookFormError('');
  };

  // --- User Billing & Credits helpers (platform admin only) ---

  const fetchBillingSnapshot = async (userId) => {
    if (!userId) return;
    setBillingSnapshotLoading(true);
    setBillingSnapshotError('');
    setBillingSnapshot(null);
    try {
      const [creditsRes, subRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/admin/users/${userId}/credits`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
        }),
        fetch(`${API_BASE_URL}/api/admin/users/${userId}/subscription`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
        }),
      ]);
      if (!creditsRes.ok || !subRes.ok) throw new Error('Failed to load user billing data');
      const credits = await creditsRes.json();
      const sub = await subRes.json();
      setBillingSnapshot({ ...credits, ...sub });
    } catch (err) {
      setBillingSnapshotError(err.message || 'Failed to load billing data');
    } finally {
      setBillingSnapshotLoading(false);
    }
  };

  const fetchCurrentCustomerCode = async (userId) => {
    if (!userId) return;
    setCurrentCustomerCodeLoading(true);
    setCurrentCustomerCodeError('');
    setCurrentCustomerCode(null);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/payment/admin/users/${userId}/customer-code`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } },
      );
      if (!res.ok) throw new Error('Failed to load Helcim customer code');
      setCurrentCustomerCode(await res.json());
    } catch (err) {
      setCurrentCustomerCodeError(err.message || 'Failed to load Helcim customer code');
    } finally {
      setCurrentCustomerCodeLoading(false);
    }
  };

  const fetchAdminPaymentMethods = async (userId) => {
    if (!userId) return;
    setPaymentMethodsLoading(true);
    setPaymentMethodsError('');
    setPaymentMethods([]);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/users/${userId}/payment-methods`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
      });
      if (!res.ok) throw new Error('Failed to fetch payment methods');
      const data = await res.json();
      setPaymentMethods(Array.isArray(data?.payment_methods) ? data.payment_methods : []);
    } catch (err) {
      setPaymentMethodsError(err.message);
    } finally {
      setPaymentMethodsLoading(false);
    }
  };

  React.useEffect(() => {
    if (selectedBillingUserId) {
      setCreditAmount('');
      setCreditAdjustError('');
      setCreditAdjustSuccess('');
      setCustomerCodeError('');
      setCustomerCodeSuccess('');
      setSyncError('');
      setSyncSuccess('');
      fetchBillingSnapshot(selectedBillingUserId);
      fetchAdminPaymentMethods(selectedBillingUserId);
      fetchCurrentCustomerCode(selectedBillingUserId);
    } else {
      setBillingSnapshot(null);
      setPaymentMethods([]);
      setCustomerCode('');
      setCurrentCustomerCode(null);
      setCurrentCustomerCodeError('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBillingUserId]);

  const handleCreditAdjust = async () => {
    const amount = parseFloat(creditAmount);
    if (isNaN(amount) || amount === 0) {
      setCreditAdjustError('Enter a non-zero amount (positive to add, negative to deduct).');
      return;
    }
    setCreditAdjustLoading(true);
    setCreditAdjustError('');
    setCreditAdjustSuccess('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/users/${selectedBillingUserId}/credits/add`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ amount }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.detail || 'Failed to adjust credits');
      setCreditAdjustSuccess(
        `Adjustment applied. Balance updated from $${data.previous_balance.toFixed(2)} ` +
        `to $${data.new_balance.toFixed(2)}. Assistants re-synced across all channels.`
      );
      setCreditAmount('');
      fetchBillingSnapshot(selectedBillingUserId);
      // Refresh the credits badge for this user in the table
      setPageCredits(prev => ({ ...prev, [selectedBillingUserId]: data.new_balance }));
    } catch (err) {
      setCreditAdjustError(err.message);
    } finally {
      setCreditAdjustLoading(false);
    }
  };

  const handleSaveCustomerCode = async () => {
    const code = customerCode.trim();
    if (!code) {
      setCustomerCodeError('Enter a Helcim customer code before saving.');
      return;
    }
    setCustomerCodeLoading(true);
    setCustomerCodeError('');
    setCustomerCodeSuccess('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/payment/admin/users/${selectedBillingUserId}/customer-code`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ customer_code: code }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.detail || 'Failed to save customer code');
      setCustomerCodeSuccess(`Customer code saved and Helcim profile synced for user ${selectedBillingUserId}.`);
      setCustomerCode('');
      fetchAdminPaymentMethods(selectedBillingUserId);
      // Re-read so the panel shows the code that is now actually on file
      // rather than the one it displayed before the overwrite.
      fetchCurrentCustomerCode(selectedBillingUserId);
    } catch (err) {
      setCustomerCodeError(err.message);
    } finally {
      setCustomerCodeLoading(false);
    }
  };

  const handleAgentSync = async (action) => {
    setSyncLoading(true);
    setSyncError('');
    setSyncSuccess('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/users/${selectedBillingUserId}/agents/sync`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.detail || `Failed to ${action} agents`);
      setSyncSuccess(data.message || `Agents ${action}d successfully.`);
    } catch (err) {
      setSyncError(err.message);
    } finally {
      setSyncLoading(false);
    }
  };

  // --- Business agent member access helpers ---

  const fetchBusinessAgents = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/business-assistants`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) return;
      const data = await response.json();
      const assistants = Array.isArray(data?.assistants)
        ? data.assistants
        : Array.isArray(data)
          ? data
          : [];
      // Only the agent's creator (or a platform admin) can manage member
      // access. Restrict the list so we never request members for an agent
      // the admin doesn't own, that would 403.
      const nextBusinessAgents = assistants.filter(
        (agent) =>
          agent.agent_type === 'business' &&
          (isPlatformAdmin || agent.created_by_user_id === userId)
      );
      setBusinessAgents(nextBusinessAgents);
      setSelectedAgentId((current) =>
        nextBusinessAgents.some((agent) => agent.id === current)
          ? current
          : nextBusinessAgents[0]?.id || ''
      );
    } catch {
      // non-critical, silently fail
    }
  };

  const fetchAgentMembers = async (agentId) => {
    if (!agentId) return;
    setAgentAccessLoading(true);
    setAgentAccessError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/business-assistants/${agentId}/members`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        // Non-critical: the agent may have no manageable members or be
        // inaccessible. Show an empty list rather than an error banner.
        setAgentMembers([]);
        return;
      }
      setAgentMembers(await response.json());
    } catch {
      setAgentMembers([]);
    } finally {
      setAgentAccessLoading(false);
    }
  };

  const handleAddMemberAccess = async (agentId, userId) => {
    setAgentAccessError('');
    setAgentAccessSuccess('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/business-assistants/${agentId}/members`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ user_id: parseInt(userId, 10) }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => null);
        throw new Error(err?.detail || 'Failed to add member');
      }
      setAgentAccessSuccess('Member access granted');
      setAddMemberUserId('');
      await fetchAgentMembers(agentId);
    } catch (err) {
      setAgentAccessError(err.message);
    }
  };

  const handleRemoveMemberAccess = async (agentId, userId) => {
    setAgentAccessError('');
    setAgentAccessSuccess('');
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/business-assistants/${agentId}/members/${userId}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json',
          },
        }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => null);
        throw new Error(err?.detail || 'Failed to remove member');
      }
      setAgentAccessSuccess('Member access revoked');
      await fetchAgentMembers(agentId);
    } catch (err) {
      setAgentAccessError(err.message);
    }
  };

  // Reload member list whenever the admin picks a different agent
  React.useEffect(() => {
    if (selectedAgentId) {
      setAgentAccessError('');
      setAgentAccessSuccess('');
      setAddMemberUserId('');
      fetchAgentMembers(selectedAgentId);
    } else {
      setAgentMembers([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAgentId]);

  // ── User Management: derived data (memos, effects, helpers) ──

  // Filtered + sorted list
  const filteredSortedUsers = useMemo(() => {
    let list = [...users];

    const q = userSearch.trim().toLowerCase();
    if (q) {
      list = list.filter(u =>
        u.email.toLowerCase().includes(q) ||
        String(u.id).includes(q) ||
        (u.contact_name || '').toLowerCase().includes(q)
      );
    }

    if (userRoleFilter === 'platform_admin') {
      list = list.filter(u => u.is_platform_admin);
    } else if (userRoleFilter === 'org_admin') {
      list = list.filter(u => !u.is_platform_admin && (u.is_admin || u.organization_role === 'ADMIN'));
    } else if (userRoleFilter === 'member') {
      list = list.filter(u => !u.is_platform_admin && !u.is_admin && u.organization_role !== 'ADMIN');
    }

    if (userOrgFilter) {
      list = list.filter(u => String(u.organization_id) === userOrgFilter);
    }

    list.sort((a, b) => {
      const aVal = userSortKey === 'id' ? Number(a.id) : String(a[userSortKey] || '').toLowerCase();
      const bVal = userSortKey === 'id' ? Number(b.id) : String(b[userSortKey] || '').toLowerCase();
      if (aVal < bVal) return userSortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return userSortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return list;
  }, [users, userSearch, userRoleFilter, userOrgFilter, userSortKey, userSortDir]);

  const userTotalPages = Math.max(1, Math.ceil(filteredSortedUsers.length / userPageSize));
  const userPageClamped = Math.min(userPage, userTotalPages);

  const pagedUsers = useMemo(
    () => filteredSortedUsers.slice((userPageClamped - 1) * userPageSize, userPageClamped * userPageSize),
    [filteredSortedUsers, userPageClamped, userPageSize]
  );

  // Unique orgs derived from the loaded users list (for org filter dropdown, platform admin only)
  const userOrgOptions = useMemo(() => {
    if (!canShowPlatformControls) return [];
    const seen = new Map();
    users.forEach(u => {
      if (u.organization_id != null && !seen.has(u.organization_id)) {
        const org = organizations.find(o => o.id === u.organization_id);
        seen.set(u.organization_id, org?.name || `Org #${u.organization_id}`);
      }
    });
    return [...seen.entries()]
      .map(([id, name]) => ({ id: String(id), name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [users, organizations, canShowPlatformControls]);

  // Reset to page 1 whenever filter/search/sort changes
  useEffect(() => {
    setUserPage(1);
  }, [userSearch, userRoleFilter, userOrgFilter, userSortKey]);

  // Keyboard shortcut: press '/' to focus the user search box when section is open
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!usersExpanded) return;
      const tag = document.activeElement?.tagName;
      if (e.key === '/' && tag !== 'INPUT' && tag !== 'TEXTAREA' && tag !== 'SELECT') {
        e.preventDefault();
        userSearchRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [usersExpanded]);

  // Lazy-load credit balances for the currently visible page (platform admin only)
  const pagedUserIds = pagedUsers.map(u => u.id).join(',');
  useEffect(() => {
    if (!canShowPlatformControls || !pagedUserIds) return;
    const ids = pagedUserIds.split(',').map(Number).filter(id => !isNaN(id));
    const token = localStorage.getItem('access_token');
    const toFetch = ids.filter(id => !fetchedCreditsRef.current.has(id));
    if (toFetch.length === 0) return;

    toFetch.forEach(userId => {
      fetchedCreditsRef.current.add(userId);
      setPageCredits(prev => ({ ...prev, [userId]: 'loading' }));
      fetch(`${API_BASE_URL}/api/admin/users/${userId}/credits`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => r.ok ? r.json() : Promise.reject())
        .then(data => setPageCredits(prev => ({
          ...prev,
          [userId]: typeof data.credit_balance === 'number' ? data.credit_balance : 'error',
        })))
        .catch(() => setPageCredits(prev => ({ ...prev, [userId]: 'error' })));
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagedUserIds, canShowPlatformControls]);

  const handleSortToggle = (key) => {
    if (userSortKey === key) {
      setUserSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setUserSortKey(key);
      setUserSortDir('asc');
    }
  };

  const handleOpenBilling = (user) => {
    setSelectedBillingUserId(String(user.id));
    setTimeout(() => {
      billingPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
  };

  // ── Derived display values ──

  const hasChanges = template !== originalTemplate;
  const hasWelcomeChanges = welcomeTemplate !== originalWelcomeTemplate;
  const isExistingOrganizationMode = newUserForm.organization_mode === 'existing';
  const selectedOrganization = canShowPlatformControls
    ? organizations.find((organization) => String(organization.id) === String(newUserForm.organization_id))
    : null;
  const organizationSummaryTitle = canShowPlatformControls
    ? isExistingOrganizationMode
      ? selectedOrganization?.name || 'Select an organization'
      : newUserForm.organization_name.trim() || 'New organization'
    : 'Your organization';
  const organizationSummaryText = canShowPlatformControls
    ? isExistingOrganizationMode
      ? selectedOrganization
        ? 'The account will be added directly to this existing organization.'
        : 'Choose an existing organization before you submit the form.'
      : 'A brand new organization will be created from the details below.'
    : 'Users created here are automatically attached to your organization.';
  const accessSummaryLabel = newUserForm.is_platform_admin
    ? 'Platform Admin'
    : newUserForm.organization_role === 'ADMIN'
      ? 'Organization Admin'
      : 'Member';
  const accessSummaryText = newUserForm.is_platform_admin
    ? 'Full platform-wide access, plus the selected organization role.'
    : newUserForm.organization_role === 'ADMIN'
      ? 'Can manage organization users, settings, and operational access.'
      : 'Standard workspace access without organization-level admin controls.';
  const createUserButtonLabel = canShowPlatformControls ? 'Create User' : 'Create Organization User';
  const canSubmitCreateUser = Boolean(
    newUserForm.email.trim() &&
    !getPasswordError(newUserForm.password) &&
    (
      !canShowPlatformControls ||
      (isExistingOrganizationMode && newUserForm.organization_id) ||
      (!isExistingOrganizationMode && newUserForm.organization_name.trim())
    )
  );

  if (!canAccessAdminPage) {
    return (
      <PageContainer title="Admin" subtitle="Access denied">
        <div className="admin-page">
          <div className="admin-denied">
            <Lock aria-hidden="true" />
            <h2>You don't have access to this page</h2>
            <p>
              Admin settings are available to platform administrators and
              organization administrators. Contact your administrator if you
              think this is a mistake.
            </p>
          </div>
        </div>
      </PageContainer>
    );
  }

  if (loading) {
    return (
      <PageContainer
        loading={loading}
        loadingMessage={
          canShowPlatformControls
            ? 'Loading platform admin settings...'
            : 'Loading organization admin settings...'
        }
        title={adminPageTitle}
        subtitle={adminPageSubtitle}
      />
    );
  }

  return (
    <PageContainer
      title={adminPageTitle}
      subtitle={adminPageSubtitle}
    >
      <div className="admin-page">
      {canShowPlatformControls && (
        <div className="admin-tabs" role="tablist" aria-label="Admin sections">
          {ADMIN_TABS.map(tab => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              id={`admin-tab-${tab.key}`}
              aria-selected={activeTab === tab.key}
              aria-controls={`admin-panel-${tab.key}`}
              className={`admin-tab ${activeTab === tab.key ? 'is-active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {canShowPlatformControls && activeTab === 'capabilities' && (
        <section
          className="account-section"
          id="admin-panel-capabilities"
          role="tabpanel"
          aria-labelledby="admin-tab-capabilities"
        >
          <h2 className="section-header">
            <Zap className="admin-section-icon" />
            Capability Management
          </h2>
          <p className="admin-section-hint">
            Define the capabilities assistants can be granted, and which plans include them.
          </p>
        </section>
      )}

      {canShowPlatformControls && activeTab === 'general' && (
        <div id="admin-panel-general" role="tabpanel" aria-labelledby="admin-tab-general">
          <section className="account-section">
            <h2 className="section-header">
              <Settings className="admin-section-icon" />
              Assistant Prompt Template
            </h2>
            <p className="admin-section-hint">
              The base prompt every assistant inherits. Variables below are substituted per assistant.
            </p>
            {settingsError && <div className="save-message error">{settingsError}</div>}
            {settingsSuccess && <div className="save-message success">{settingsSuccess}</div>}
            <div className="grid-container">
              <div className="input-group full-width">
                <textarea
                  className="prompt-textarea"
                  value={template}
                  onChange={(event) => setTemplate(event.target.value)}
                  placeholder="Enter your assistant prompt template"
                />
              </div>
              <div className="input-group full-width">
                <div className="admin-info-box">
                  <strong>Available Variables:</strong>{' '}
                  {'{'}assistant_name{'}'}, {'{'}business_name{'}'}, {'{'}business_type{'}'}, {'{'}location{'}'}
                </div>
              </div>
            </div>
            <div className="admin-button-row">
              <button
                className="primary-button"
                onClick={handleSave}
                disabled={!hasChanges || saving}
              >
                {saving ? 'Saving' : 'Save Changes'}
              </button>
              <button
                className="admin-btn-cancel"
                onClick={handleCancel}
                disabled={!hasChanges || saving}
              >
                Cancel
              </button>
            </div>
          </section>

          {/* ── Welcome Announcement ── */}
          <section className="account-section admin-section-mt">
            <h2 className="section-header">
              <MessageCircle className="admin-section-icon" />
              Welcome Announcement Template
            </h2>
            <p className="admin-section-hint">
              Sent to a new user the first time their assistant is provisioned.
            </p>
            {welcomeError && (
              <div className="save-message error">{welcomeError}</div>
            )}
            {welcomeSuccessMessage && (
              <div className="save-message success">{welcomeSuccessMessage}</div>
            )}
            <div className="grid-container">
              <div className="input-group full-width">
                <textarea
                  className="prompt-textarea admin-welcome-textarea"
                  value={welcomeTemplate}
                  onChange={(event) => setWelcomeTemplate(event.target.value)}
                  placeholder="Enter your welcome announcement template"
                />
              </div>
              <div className="input-group full-width">
                <div className="admin-info-box">
                  <strong>Formatting Tips:</strong>
                  <ul className="admin-info-list">
                    <li>Wrap text in *asterisks* to render it bold.</li>
                    <li>
                      The placeholder [Insert AI Phone Number Here] is replaced
                      automatically with the assistant's number.
                    </li>
                    <li>Unicode characters, including emoji, are supported.</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="admin-button-row">
              <button
                className="primary-button"
                onClick={handleSaveWelcome}
                disabled={!hasWelcomeChanges || savingWelcome}
              >
                {savingWelcome ? 'Saving' : 'Save Changes'}
              </button>
              <button
                className="admin-btn-cancel"
                onClick={handleCancelWelcome}
                disabled={!hasWelcomeChanges || savingWelcome}
              >
                Cancel
              </button>
            </div>
          </section>
        </div>
      )}

      {(!canShowPlatformControls || activeTab === 'users') && (
      <div id="admin-panel-users" role="tabpanel" aria-labelledby="admin-tab-users">
      {/* ── Create User ── */}
      <section className="account-section admin-section-mt">
        <button
          className="admin-collapsible-header"
          onClick={() => setCreateUserExpanded(v => !v)}
          aria-expanded={createUserExpanded}
        >
          <h2 className="section-header">
            <UserPlus className="admin-section-icon" />
            {canShowPlatformControls ? 'Create User' : 'Create Organization User'}
          </h2>
          {createUserExpanded
            ? <ChevronDown className="admin-collapse-chevron" />
            : <ChevronRight className="admin-collapse-chevron" />
          }
        </button>
        {createUserExpanded && (
          <>
            <div className="admin-create-user-header admin-section-mt-sm">
              <div>
                <p className="section-description">
                  {canShowPlatformControls
                    ? 'Create an account, assign it to an organization, and set its access level.'
                    : 'Add a user to your organization and set their role.'}
                </p>
              </div>
              <span className={`admin-scope-badge ${canShowPlatformControls ? '' : 'admin-scope-badge--org'}`}>
                {canShowPlatformControls ? 'Platform Scope' : 'Organization Scope'}
              </span>
            </div>
            {createUserError && <div className="save-message error">{createUserError}</div>}
            {createUserSuccess && <div className="save-message success">{createUserSuccess}</div>}
            <form onSubmit={handleCreateUser} className="admin-create-user-form">
              <div className="admin-create-user-panel">
                <div className="admin-create-user-panel-header">
                  <h3 className="admin-create-user-panel-title">Account Details</h3>
                  <p className="admin-create-user-panel-description">
                    Sign-in credentials and contact details for the new account.
                  </p>
                </div>
                <div className="grid-container admin-create-user-grid">
                  <div className="input-group">
                    <label htmlFor="new-user-email">Email</label>
                    <input
                      id="new-user-email"
                      name="email"
                      type="email"
                      value={newUserForm.email}
                      onChange={handleCreateUserChange}
                      required
                      autoComplete="email"
                      placeholder="user@example.com"
                    />
                    <p className="admin-field-hint">Used as the primary login for this account.</p>
                  </div>
                  <div className="input-group">
                    <label htmlFor="new-user-password">Password</label>
                    <input
                      id="new-user-password"
                      name="password"
                      type="password"
                      minLength="8"
                      value={newUserForm.password}
                      onChange={handleCreateUserChange}
                      required
                      autoComplete="new-password"
                      placeholder="Min 8 chars, upper, lower, number & symbol"
                    />
                    <p className="admin-field-hint">
                      Must be at least 8 characters and include an uppercase letter, a lowercase
                      letter, a number, and a special character.
                    </p>
                  </div>
                  <div className="input-group">
                    <label htmlFor="new-user-contact-name">Contact Name</label>
                    <input
                      id="new-user-contact-name"
                      name="contact_name"
                      type="text"
                      value={newUserForm.contact_name}
                      onChange={handleCreateUserChange}
                      autoComplete="name"
                      placeholder="Full name"
                    />
                  </div>
                  <div className="input-group">
                    <label htmlFor="new-user-contact-phone">Contact Phone</label>
                    <input
                      id="new-user-contact-phone"
                      name="contact_phone"
                      type="tel"
                      value={newUserForm.contact_phone}
                      onChange={handleCreateUserChange}
                      autoComplete="tel"
                      placeholder="+1 555 000 0000"
                    />
                  </div>
                </div>
              </div>

              {canShowPlatformControls && (
                <div className="admin-create-user-panel">
                  <div className="admin-create-user-panel-header">
                    <h3 className="admin-create-user-panel-title">Organization Assignment</h3>
                    <p className="admin-create-user-panel-description">
                      Add this account to an existing organization, or create a new one for it.
                    </p>
                  </div>

                  <div className="admin-create-user-mode-grid">
                    <label className={`admin-create-user-mode-card ${isExistingOrganizationMode ? 'is-selected' : ''}`}>
                      <span className="admin-create-user-mode-title-row">
                        <input
                          type="radio"
                          name="organization_mode"
                          value="existing"
                          checked={isExistingOrganizationMode}
                          onChange={handleCreateUserChange}
                        />
                        <span className="admin-create-user-mode-title">Use Existing Organization</span>
                      </span>
                      <span className="admin-create-user-mode-copy">
                        Add this user to an organization that already exists on the platform.
                      </span>
                    </label>

                    <label className={`admin-create-user-mode-card ${!isExistingOrganizationMode ? 'is-selected' : ''}`}>
                      <span className="admin-create-user-mode-title-row">
                        <input
                          type="radio"
                          name="organization_mode"
                          value="new"
                          checked={!isExistingOrganizationMode}
                          onChange={handleCreateUserChange}
                        />
                        <span className="admin-create-user-mode-title">Create New Organization</span>
                      </span>
                      <span className="admin-create-user-mode-copy">
                        Create the organization first, then attach the user to it in one step.
                      </span>
                    </label>
                  </div>

                  {isExistingOrganizationMode ? (
                    <div className="grid-container admin-create-user-grid">
                      <div className="input-group full-width">
                        <label htmlFor="new-user-organization-id">Organization</label>
                        <select
                          id="new-user-organization-id"
                          name="organization_id"
                          className={newUserForm.organization_id ? '' : 'is-placeholder'}
                          value={newUserForm.organization_id}
                          onChange={handleCreateUserChange}
                          required={isExistingOrganizationMode}
                        >
                          <option value="">
                            {orgsLoading ? 'Loading organizations' : 'Select an organization'}
                          </option>
                          {adminOrgId && organizations.find((organization) => organization.id === adminOrgId) && (
                            <option key={`own-${adminOrgId}`} value={adminOrgId}>
                              {organizations.find((organization) => organization.id === adminOrgId).name} (Your Organization)
                            </option>
                          )}
                          {organizations
                            .filter((organization) => organization.id !== adminOrgId)
                            .map((organization) => (
                              <option key={organization.id} value={organization.id}>
                                {organization.name} (ID: {organization.id})
                              </option>
                            ))}
                        </select>
                        <p className="admin-field-hint">
                          {selectedOrganization
                            ? `${selectedOrganization.name} will receive this user with the selected organization role.`
                            : 'Select the organization that should own this account.'}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="grid-container admin-create-user-grid">
                      <div className="input-group">
                        <label htmlFor="new-org-name">Organization Name</label>
                        <input
                          id="new-org-name"
                          name="organization_name"
                          type="text"
                          value={newUserForm.organization_name}
                          onChange={handleCreateUserChange}
                          autoComplete="organization"
                          placeholder="e.g. Acme Corp"
                          required={!isExistingOrganizationMode}
                        />
                        <p className="admin-field-hint">This becomes the new organization name shown across the platform.</p>
                      </div>
                      <div className="input-group">
                        <label htmlFor="new-org-url">Organization Website</label>
                        <input
                          id="new-org-url"
                          name="organization_url"
                          type="url"
                          value={newUserForm.organization_url}
                          onChange={handleCreateUserChange}
                          autoComplete="url"
                          placeholder="https://example.com"
                        />
                        <p className="admin-field-hint">Optional, but helpful for business context and onboarding metadata.</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="admin-create-user-panel">
                <div className="admin-create-user-panel-header">
                  <h3 className="admin-create-user-panel-title">Permissions</h3>
                  <p className="admin-create-user-panel-description">
                    Set this user's role within the organization, and their platform-wide access.
                  </p>
                </div>
                <div className="grid-container admin-create-user-grid">
                  <div className="input-group">
                    <label htmlFor="new-user-organization-role">Organization Role</label>
                    <select
                      id="new-user-organization-role"
                      name="organization_role"
                      value={newUserForm.organization_role}
                      onChange={handleCreateUserChange}
                    >
                      <option value="ADMIN">Organization Admin</option>
                      <option value="MEMBER">Member</option>
                    </select>
                    <p className="admin-field-hint">
                      {newUserForm.organization_role === 'ADMIN'
                        ? 'Admins can manage users, settings, and organization-level access.'
                        : 'Members can work inside the organization without administrative controls.'}
                    </p>
                  </div>

                  {canShowPlatformControls && (
                    <div className="input-group full-width">
                      <label className={`admin-create-user-toggle ${newUserForm.is_platform_admin ? 'is-selected' : ''}`}>
                        <input
                          name="is_platform_admin"
                          type="checkbox"
                          checked={newUserForm.is_platform_admin}
                          onChange={handleCreateUserChange}
                        />
                        <span>
                          <strong>Grant Platform Admin access</strong>
                          <small>Enables platform-wide management and visibility beyond a single organization.</small>
                        </span>
                      </label>
                    </div>
                  )}
                </div>
              </div>

              {/* Review line, directly above the commit button. This content
                  used to sit in two cards at the top of the form, where it
                  narrated decisions the user had not made yet ("Choose an
                  existing organization before you submit"). At the footer it
                  is a genuine pre-submit summary of what is about to be
                  created. */}
              <div className="admin-create-user-review">
                <div className="admin-create-user-review-item">
                  <span className="admin-create-user-review-label">Organization</span>
                  <strong>{organizationSummaryTitle}</strong>
                  <p>{organizationSummaryText}</p>
                </div>
                <div className="admin-create-user-review-item">
                  <span className="admin-create-user-review-label">Access level</span>
                  <strong>{accessSummaryLabel}</strong>
                  <p>{accessSummaryText}</p>
                </div>
              </div>

              <div className="admin-button-row">
                <button
                  className="primary-button"
                  type="submit"
                  disabled={creatingUser || !canSubmitCreateUser}
                >
                  {creatingUser ? 'Creating' : createUserButtonLabel}
                </button>
                <button
                  className="admin-btn-cancel"
                  type="button"
                  onClick={resetCreateUserForm}
                  disabled={creatingUser}
                >
                  Reset Form
                </button>
              </div>
            </form>
          </>
        )}
      </section>

      {/* ── User Management ── */}
      <section className="account-section admin-section-mt">
        <button
          className="admin-collapsible-header"
          onClick={() => setUsersExpanded(v => !v)}
          aria-expanded={usersExpanded}
        >
          <h2 className="section-header">
            <Users className="admin-section-icon" />
            User Management
            {users.length > 0 && !usersLoading && (
              <span className="admin-user-count-badge">{users.length.toLocaleString()}</span>
            )}
          </h2>
          {usersExpanded
            ? <ChevronDown className="admin-collapse-chevron" />
            : <ChevronRight className="admin-collapse-chevron" />
          }
        </button>

        {usersExpanded && (
          <div className="grid-container">
            <div className="input-group full-width">
              {usersError && (
                <div className="admin-error-alert">
                  <strong>Error:</strong> {usersError}
                </div>
              )}
              {userActionError && (
                <div className="admin-error-alert">
                  <strong>Error:</strong> {userActionError}
                </div>
              )}
              {userActionSuccess && (
                <div className="save-message success">{userActionSuccess}</div>
              )}

              {usersLoading ? (
                <div className="admin-no-users">Loading users</div>
              ) : users.length > 0 ? (
                <>
                  <div className="admin-warning-box">
                    <strong>Warning:</strong> Removing a user will permanently delete their account and all associated data.
                  </div>

                  {/* ── Search / Filter Toolbar ── */}
                  <div className="admin-user-toolbar">
                    <div className="admin-user-toolbar-left">
                      <input
                        ref={userSearchRef}
                        className="admin-user-search"
                        type="search"
                        placeholder="Search by email, name or ID"
                        aria-label="Search users. Press the slash key to focus this field."
                        title="Press / to focus"
                        value={userSearch}
                        onChange={e => setUserSearch(e.target.value)}
                      />
                      <select
                        className={`admin-user-filter-select ${userRoleFilter ? '' : 'is-placeholder'}`}
                        value={userRoleFilter}
                        onChange={e => setUserRoleFilter(e.target.value)}
                        aria-label="Filter by role"
                      >
                        <option value="">All Roles</option>
                        <option value="platform_admin">Platform Admin</option>
                        <option value="org_admin">Org Admin</option>
                        <option value="member">Member</option>
                      </select>
                      {canShowPlatformControls && userOrgOptions.length > 1 && (
                        <select
                          className={`admin-user-filter-select ${userOrgFilter ? '' : 'is-placeholder'}`}
                          value={userOrgFilter}
                          onChange={e => setUserOrgFilter(e.target.value)}
                          aria-label="Filter by organization"
                        >
                          <option value="">All Orgs</option>
                          {userOrgOptions.map(org => (
                            <option key={org.id} value={org.id}>{org.name}</option>
                          ))}
                        </select>
                      )}
                      {(userSearch || userRoleFilter || userOrgFilter) && (
                        <button
                          className="admin-user-clear-btn"
                          onClick={() => { setUserSearch(''); setUserRoleFilter(''); setUserOrgFilter(''); }}
                        >
                          <X aria-hidden="true" />
                          Clear filters
                        </button>
                      )}
                    </div>
                    <div className="admin-user-toolbar-right">
                      <span className="admin-user-count-label">
                        {(userSearch || userRoleFilter || userOrgFilter)
                          ? `${filteredSortedUsers.length.toLocaleString()} of ${users.length.toLocaleString()} users`
                          : `${users.length.toLocaleString()} users`
                        }
                      </span>
                      <select
                        className="admin-user-filter-select"
                        value={userPageSize}
                        onChange={e => { setUserPageSize(Number(e.target.value)); setUserPage(1); }}
                        aria-label="Rows per page"
                      >
                        <option value={10}>10 / page</option>
                        <option value={25}>25 / page</option>
                        <option value={50}>50 / page</option>
                        <option value={100}>100 / page</option>
                      </select>
                    </div>
                  </div>

                  {/* ── User Table ── */}
                  {filteredSortedUsers.length === 0 ? (
                    <div className="admin-no-users">No users match the current filters.</div>
                  ) : (
                    <>
                      <div className="admin-table-wrapper">
                        <table className="admin-table">
                          <thead>
                            <tr>
                              <th aria-sort={userSortKey === 'id' ? (userSortDir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                                <button
                                  className={`admin-sort-btn ${userSortKey === 'id' ? 'is-active' : ''}`}
                                  onClick={() => handleSortToggle('id')}
                                >
                                  ID
                                  <span className="admin-sort-icon" aria-hidden="true">
                                    {userSortKey === 'id' && (
                                      userSortDir === 'asc' ? <ArrowUp /> : <ArrowDown />
                                    )}
                                  </span>
                                </button>
                              </th>
                              <th aria-sort={userSortKey === 'email' ? (userSortDir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                                <button
                                  className={`admin-sort-btn ${userSortKey === 'email' ? 'is-active' : ''}`}
                                  onClick={() => handleSortToggle('email')}
                                >
                                  Email
                                  <span className="admin-sort-icon" aria-hidden="true">
                                    {userSortKey === 'email' && (
                                      userSortDir === 'asc' ? <ArrowUp /> : <ArrowDown />
                                    )}
                                  </span>
                                </button>
                              </th>
                              <th>Role</th>
                              {canShowPlatformControls && <th className="admin-col-numeric">Credits</th>}
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pagedUsers.map(user => {
                              const roleLabel = user.is_platform_admin
                                ? 'Platform Admin'
                                : (user.is_admin || user.organization_role === 'ADMIN')
                                  ? 'Org Admin'
                                  : 'Member';
                              const roleKey = user.is_platform_admin
                                ? 'platform'
                                : (user.is_admin || user.organization_role === 'ADMIN')
                                  ? 'admin'
                                  : 'member';
                              const creditVal = pageCredits[user.id];
                              return (
                                <tr key={user.id}>
                                  <td className="admin-cell-id">{user.id}</td>
                                  <td className="admin-cell-email">{user.email}</td>
                                  <td>
                                    <span className={`admin-role-badge admin-role-badge--${roleKey}`}>
                                      {roleLabel}
                                    </span>
                                  </td>
                                  {canShowPlatformControls && (
                                    <td className="admin-col-numeric">
                                      {creditVal === undefined || creditVal === 'error'
                                        ? <span className="admin-cell-empty">-</span>
                                        : creditVal === 'loading'
                                          ? <span className="admin-cell-empty">…</span>
                                          : (
                                            <span className={`admin-credit-badge ${
                                              creditVal < 0
                                                ? 'admin-credit-badge--negative'
                                                : creditVal === 0
                                                  ? 'admin-credit-badge--zero'
                                                  : 'admin-credit-badge--positive'
                                            }`}>
                                              ${creditVal.toFixed(2)}
                                            </span>
                                          )
                                      }
                                    </td>
                                  )}
                                  <td>
                                    <div className="admin-action-buttons">
                                      {canShowUserManagementActions && (
                                        <button
                                          className="admin-btn-switch"
                                          onClick={() => handleSwitchUser(user)}
                                          disabled={switchingUserId !== null}
                                        >
                                          {switchingUserId === user.id ? 'Switching' : 'Switch'}
                                        </button>
                                      )}
                                      {canShowPlatformControls && (
                                        <button
                                          className="admin-btn-billing"
                                          onClick={() => handleOpenBilling(user)}
                                          title="Manage credits & billing"
                                        >
                                          Billing
                                        </button>
                                      )}
                                      {user.id !== 23 && (
                                        <button
                                          className="admin-btn-remove"
                                          onClick={() => {
                                            setSelectedUser(user);
                                            setShowConfirmation(true);
                                          }}
                                          disabled={removingUserId !== null}
                                        >
                                          {removingUserId === user.id ? 'Removing' : 'Remove'}
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* ── Pagination ── */}
                      {userTotalPages > 1 && (
                        <div className="admin-pagination">
                          <button
                            className="admin-pagination-btn"
                            onClick={() => setUserPage(1)}
                            disabled={userPageClamped === 1}
                            aria-label="First page"
                          >
                            <ChevronsLeft aria-hidden="true" />
                          </button>
                          <button
                            className="admin-pagination-btn"
                            onClick={() => setUserPage(p => Math.max(1, p - 1))}
                            disabled={userPageClamped === 1}
                          >
                            <ChevronLeft aria-hidden="true" />
                            Previous
                          </button>
                          <span className="admin-pagination-info">
                            Page {userPageClamped} of {userTotalPages}
                            <span className="admin-pagination-range">
                              ({((userPageClamped - 1) * userPageSize) + 1}–{Math.min(userPageClamped * userPageSize, filteredSortedUsers.length)})
                            </span>
                          </span>
                          <button
                            className="admin-pagination-btn"
                            onClick={() => setUserPage(p => Math.min(userTotalPages, p + 1))}
                            disabled={userPageClamped === userTotalPages}
                          >
                            Next
                            <ChevronRight aria-hidden="true" />
                          </button>
                          <button
                            className="admin-pagination-btn"
                            onClick={() => setUserPage(userTotalPages)}
                            disabled={userPageClamped === userTotalPages}
                            aria-label="Last page"
                          >
                            <ChevronsRight aria-hidden="true" />
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </>
              ) : (
                <div className="admin-no-users">No users found</div>
              )}

              {/* ── Remove confirmation dialog ── */}
              {showConfirmation && selectedUser && (
                <div className="admin-confirmation-box">
                  <div className="admin-confirmation-warning">
                    <div>
                      Are you absolutely sure you want to remove user {selectedUser.email} (ID: {selectedUser.id})? This action cannot be undone.
                    </div>
                    <label className="admin-checkbox-label">
                      <input
                        type="checkbox"
                        checked={deleteAssistants}
                        onChange={(e) => setDeleteAssistants(e.target.checked)}
                      />
                      Also delete associated AI assistants
                    </label>
                  </div>
                  <div className="admin-button-row">
                    <button
                      className="admin-btn-remove admin-btn-lg"
                      onClick={removeUser}
                      disabled={removing}
                    >
                      {removing ? 'Removing User' : 'Yes, Remove User'}
                    </button>
                    <button
                      className="admin-btn-cancel admin-btn-lg"
                      onClick={() => {
                        setShowConfirmation(false);
                        setSelectedUser(null);
                        setDeleteAssistants(false);
                      }}
                      disabled={removing}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* ── Platform Admin: User Credits & Billing ── */}
              {canShowPlatformControls && (
                <div ref={billingPanelRef} className="admin-billing-panel">
                  <h3 className="section-header admin-subsection-header">
                    <Zap className="admin-section-icon" />
                    User Credits &amp; Billing
                  </h3>

                  {selectedBillingUserId ? (
                    <div className="admin-billing-managing">
                      <div className="admin-billing-managing-info">
                        <span className="admin-billing-managing-label">Managing billing for:</span>
                        <strong>
                          {users.find(u => String(u.id) === String(selectedBillingUserId))?.email
                            || `User #${selectedBillingUserId}`}
                        </strong>
                        <span className="admin-billing-managing-id">(ID: {selectedBillingUserId})</span>
                      </div>
                      <button
                        className="admin-user-clear-btn"
                        onClick={() => setSelectedBillingUserId('')}
                      >
                        <X aria-hidden="true" />
                        Close
                      </button>
                    </div>
                  ) : (
                    <div className="admin-billing-placeholder">
                      Select a user via the <strong>Billing</strong> action above.
                    </div>
                  )}

                  {billingSnapshotLoading && (
                    <div className="admin-muted-note admin-muted-note--mb">Loading billing data</div>
                  )}
                  {billingSnapshotError && (
                    <div className="admin-status-box admin-status-box--error admin-status-box--mb">
                      {billingSnapshotError}
                    </div>
                  )}

                  {billingSnapshot && !billingSnapshotLoading && (
                    <>
                      {/* Read-only snapshot */}
                      <div className="admin-create-user-summary">
                        <div className="admin-create-user-summary-card">
                          <span className="admin-create-user-summary-label">Credit Balance</span>
                          <strong className="admin-summary-value-lg">${(billingSnapshot.credit_balance || 0).toFixed(2)}</strong>
                        </div>
                        <div className="admin-create-user-summary-card">
                          <span className="admin-create-user-summary-label">Subscription</span>
                          <strong>{billingSnapshot.subscription_status || '-'}</strong>
                          <p>
                            {billingSnapshot.plan_type || '-'} · {billingSnapshot.billing_cycle || '-'}
                          </p>
                        </div>
                        <div className="admin-create-user-summary-card">
                          <span className="admin-create-user-summary-label">Trial End</span>
                          <strong>
                            {billingSnapshot.trial_end
                              ? new Date(billingSnapshot.trial_end).toLocaleDateString()
                              : '-'}
                          </strong>
                        </div>
                        {billingSnapshot.subscription_id && (
                          <div className="admin-create-user-summary-card">
                            <span className="admin-create-user-summary-label">Subscription ID</span>
                            <strong className="admin-summary-value-id">
                              {billingSnapshot.subscription_id}
                            </strong>
                          </div>
                        )}
                      </div>

                      <div className="admin-panel-stack">
                      {/* ── Adjust Credits ── */}
                      <div className="admin-create-user-panel">
                        <div className="admin-create-user-panel-header">
                          <h3 className="admin-create-user-panel-title">Adjust Credits</h3>
                          <p className="admin-create-user-panel-description">
                            Positive adds credit, negative deducts. Assistants re-sync across all channels afterwards.
                          </p>
                        </div>
                        {creditAdjustError && (
                          <div className="admin-status-box admin-status-box--error admin-status-box--mb-sm">{creditAdjustError}</div>
                        )}
                        {creditAdjustSuccess && (
                          <div className="admin-status-box admin-status-box--success admin-status-box--mb-sm">{creditAdjustSuccess}</div>
                        )}
                        <div className="grid-container admin-create-user-grid">
                          <div className="input-group">
                            <label htmlFor="credit-amount">Amount ($)</label>
                            <input
                              id="credit-amount"
                              type="number"
                              step="0.01"
                              value={creditAmount}
                              onChange={e => { setCreditAmount(e.target.value); setCreditAdjustError(''); setCreditAdjustSuccess(''); }}
                              placeholder="e.g. 25.00 or -10.00"
                            />
                          </div>
                        </div>
                        <div className="admin-button-row">
                          <button
                            className="primary-button"
                            onClick={handleCreditAdjust}
                            disabled={creditAdjustLoading || !creditAmount}
                          >
                            {creditAdjustLoading ? 'Applying' : 'Apply Adjustment'}
                          </button>
                        </div>
                      </div>

                      {/* ── Helcim Customer Code ── */}
                      <div className="admin-create-user-panel">
                        <div className="admin-create-user-panel-header">
                          <h3 className="admin-create-user-panel-title">Helcim Customer Code</h3>
                          <p className="admin-create-user-panel-description">
                            Links this user to their Helcim profile so saved cards and charges resolve.
                          </p>
                        </div>
                        {customerCodeError && (
                          <div className="admin-status-box admin-status-box--error admin-status-box--mb-sm">{customerCodeError}</div>
                        )}
                        {customerCodeSuccess && (
                          <div className="admin-status-box admin-status-box--success admin-status-box--mb-sm">{customerCodeSuccess}</div>
                        )}

                        {/* What is on file right now. Shown before the input so
                            the code is read before it can be overwritten, and
                            with its dates so an old Helcim profile is
                            distinguishable from a freshly linked one. */}
                        {currentCustomerCodeLoading && (
                          <div className="admin-muted-note admin-muted-note--mb">Loading current customer code</div>
                        )}
                        {currentCustomerCodeError && (
                          <div className="admin-status-box admin-status-box--error admin-status-box--mb-sm">
                            {currentCustomerCodeError}
                          </div>
                        )}
                        {currentCustomerCode && !currentCustomerCodeLoading && (
                          <div className="admin-helcim-current">
                            {currentCustomerCode.customer_code ? (
                              <>
                                <div className="admin-helcim-current-row">
                                  <span className="admin-helcim-current-label">Current code</span>
                                  <code className="admin-helcim-current-code">
                                    {currentCustomerCode.customer_code}
                                  </code>
                                </div>
                                <div className="admin-helcim-current-meta">
                                  <span>
                                    Linked{' '}
                                    {currentCustomerCode.created_at
                                      ? new Date(currentCustomerCode.created_at).toLocaleDateString()
                                      : 'unknown'}
                                  </span>
                                  <span>
                                    Last changed{' '}
                                    {currentCustomerCode.updated_at
                                      ? new Date(currentCustomerCode.updated_at).toLocaleDateString()
                                      : 'unknown'}
                                  </span>
                                </div>
                              </>
                            ) : (
                              <div className="admin-helcim-current-row">
                                <span className="admin-helcim-current-label">Current code</span>
                                <span className="admin-helcim-current-empty">
                                  None on file, this user has no Helcim profile linked yet.
                                </span>
                              </div>
                            )}
                          </div>
                        )}

                        <div className="grid-container admin-create-user-grid">
                          <div className="input-group full-width">
                            <label htmlFor="helcim-customer-code">New Customer Code</label>
                            <input
                              id="helcim-customer-code"
                              type="text"
                              value={customerCode}
                              onChange={e => { setCustomerCode(e.target.value); setCustomerCodeError(''); setCustomerCodeSuccess(''); }}
                              placeholder="e.g. CST-XXXXXXXX"
                              disabled={customerCodeLoading}
                            />
                          </div>
                        </div>
                        <div className="admin-button-row">
                          <button
                            className="primary-button"
                            onClick={handleSaveCustomerCode}
                            disabled={customerCodeLoading || !customerCode.trim()}
                          >
                            {customerCodeLoading ? 'Saving' : 'Save Customer Code'}
                          </button>
                        </div>
                      </div>

                      {/* ── Agent Channel Sync ── */}
                      <div className="admin-create-user-panel">
                        <div className="admin-create-user-panel-header">
                          <h3 className="admin-create-user-panel-title">Agent Channel Sync</h3>
                          <p className="admin-create-user-panel-description">
                            Enable or disable every assistant this user owns, across all channels at once.
                          </p>
                        </div>
                        {syncError && (
                          <div className="admin-status-box admin-status-box--error admin-status-box--mb-sm">{syncError}</div>
                        )}
                        {syncSuccess && (
                          <div className="admin-status-box admin-status-box--success admin-status-box--mb-sm">{syncSuccess}</div>
                        )}
                        <div className="admin-button-row">
                          <button
                            className="primary-button"
                            onClick={() => handleAgentSync('enable')}
                            disabled={syncLoading}
                          >
                            {syncLoading ? 'Syncing' : 'Enable All Agents'}
                          </button>
                          <button
                            className="admin-btn-cancel"
                            onClick={() => handleAgentSync('disable')}
                            disabled={syncLoading}
                          >
                            {syncLoading ? 'Syncing' : 'Disable All Agents'}
                          </button>
                        </div>
                      </div>

                      {/* ── Saved Payment Methods (read-only) ── */}
                      <div className="admin-create-user-panel">
                        <div className="admin-create-user-panel-header">
                          <h3 className="admin-create-user-panel-title">Saved Payment Methods</h3>
                        </div>
                        {paymentMethodsLoading && <div className="admin-muted-note">Loading</div>}
                        {paymentMethodsError && (
                          <div className="admin-status-box admin-status-box--error">{paymentMethodsError}</div>
                        )}
                        {!paymentMethodsLoading && !paymentMethodsError && paymentMethods.length === 0 && (
                          <div className="admin-no-users">No payment methods on file.</div>
                        )}
                        {!paymentMethodsLoading && paymentMethods.length > 0 && (
                          <div className="admin-table-wrapper">
                            <table className="admin-table">
                              <thead>
                                <tr>
                                  <th>Brand</th>
                                  <th>Last 4</th>
                                  <th>Expires</th>
                                  <th>Default</th>
                                </tr>
                              </thead>
                              <tbody>
                                {paymentMethods.map((card, idx) => (
                                  <tr key={card.card_id ?? idx}>
                                    <td>{card.card_type || card.brand || '-'}</td>
                                    <td>••••&nbsp;{card.last_four || card.last4 || '-'}</td>
                                    <td>{card.expiry_month || '-'}/{card.expiry_year || '-'}</td>
                                    <td>
                                      {card.is_default
                                        ? <span className="webhook-status-badge webhook-status-active">Default</span>
                                        : '-'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </section>

      {/* ── Business Assistant Member Access (org admins only) ── */}
      {isOrganizationAdmin && (
        <section className="account-section admin-section-mt">
          <button
            className="admin-collapsible-header"
            onClick={() => setAgentAccessExpanded(v => !v)}
            aria-expanded={agentAccessExpanded}
          >
            <h2 className="section-header">
              <Briefcase className="admin-section-icon" />
              Business Assistant Member Access
            </h2>
            {agentAccessExpanded
              ? <ChevronDown className="admin-collapse-chevron" />
              : <ChevronRight className="admin-collapse-chevron" />
            }
          </button>

          {agentAccessExpanded && (
            <div className="grid-container">
              <div className="input-group full-width">
                <div className="admin-info-box admin-info-box--mb">
                  By default, all organization members can see every active business assistant. Once you
                  grant explicit access to <strong>any</strong> member for
                  an agent, only those members (plus org admins) will see it.
                </div>

                {agentAccessError && (
                  <div className="admin-status-box admin-status-box--error admin-status-box--mb">{agentAccessError}</div>
                )}
                {agentAccessSuccess && (
                  <div className="admin-status-box admin-status-box--success admin-status-box--mb">{agentAccessSuccess}</div>
                )}

                {businessAgents.length === 0 ? (
                  <div className="admin-muted-note">
                    You haven't created any business assistants yet.
                  </div>
                ) : (
                  <div className="input-group admin-member-list-block">
                    <label>Select Business Assistant</label>
                    <select
                      className={selectedAgentId ? '' : 'is-placeholder'}
                      value={selectedAgentId}
                      onChange={e => setSelectedAgentId(e.target.value)}
                    >
                      <option value="">Choose an agent</option>
                      {businessAgents.map(agent => (
                        <option key={agent.id} value={agent.id}>{agent.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                {selectedAgentId && (() => {
                  const selectedAgent = businessAgents.find(a => a.id === selectedAgentId);
                  const agentOrgId = selectedAgent?.organization_id ?? null;
                  return (
                    <>
                      <div className="admin-member-list-block">
                        <strong className="admin-member-list-label">Members with explicit access</strong>
                        {agentAccessLoading ? (
                          <div className="admin-muted-note admin-muted-note--mt">
                            Loading...
                          </div>
                        ) : agentMembers.length === 0 ? (
                          <div className="admin-muted-note admin-muted-note--mt">
                            None, all org members can currently see this agent.
                          </div>
                        ) : (
                          <div className="admin-table-wrapper">
                            <table className="admin-table">
                              <thead>
                                <tr>
                                  <th>Email</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {agentMembers.map(member => (
                                  <tr key={member.user_id}>
                                    <td>{member.email}</td>
                                    <td>
                                      <button
                                        className="admin-btn-remove"
                                        onClick={() => handleRemoveMemberAccess(selectedAgentId, member.user_id)}
                                      >
                                        Revoke
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>

                      <div className="admin-member-add-row">
                        <div className="input-group">
                          <label>Grant Access to Member</label>
                          <select
                            className={addMemberUserId ? '' : 'is-placeholder'}
                            value={addMemberUserId}
                            onChange={e => setAddMemberUserId(e.target.value)}
                          >
                            <option value="">Select a member</option>
                            {users
                              .filter(u =>
                                (u.organization_role === 'MEMBER' || u.organization_role === 'ADMIN') &&
                                (agentOrgId === null || u.organization_id === agentOrgId) &&
                                !agentMembers.find(m => m.user_id === u.id)
                              )
                              .map(u => (
                                <option key={u.id} value={u.id}>{u.email}</option>
                              ))
                            }
                          </select>
                        </div>
                        <button
                          className="primary-button"
                          onClick={() => handleAddMemberAccess(selectedAgentId, addMemberUserId)}
                          disabled={!addMemberUserId}
                        >
                          Grant Access
                        </button>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          )}
        </section>
      )}
      </div>)}

      {canShowPlatformControls && (
        <>
          {/* ── Event Webhooks ── */}
          <section className="account-section admin-section-mt">
            <button
              className="admin-collapsible-header"
              onClick={() => setWebhooksExpanded(v => !v)}
              aria-expanded={webhooksExpanded}
            >
              <h2 className="section-header">
                <Zap className="admin-section-icon" />
                Event Webhooks
              </h2>
              {webhooksExpanded
                ? <ChevronDown className="admin-collapse-chevron" />
                : <ChevronRight className="admin-collapse-chevron" />
              }
            </button>

            {webhooksExpanded && (
              <>
                {webhooksError && (
                  <div className="admin-error-alert">
                    <strong>Error:</strong> {webhooksError}
                  </div>
                )}
                {webhookFormSuccess && !showWebhookForm && (
                  <div className="save-message success">{webhookFormSuccess}</div>
                )}

                {/* Deleting a webhook silently stops delivery of an event to
                    an external system, which is not visible anywhere else in
                    the UI, so the confirmation names the endpoint rather
                    than asking a generic "are you sure?". */}
                {webhookPendingDelete && (
                  <div className="admin-confirmation-box">
                    <div className="admin-confirmation-warning">
                      <strong>Delete this webhook?</strong>
                      <p className="admin-confirmation-detail">
                        <span className="webhook-event-badge">{webhookPendingDelete.event_type}</span>
                        {' → '}
                        <span className="admin-confirmation-url">{webhookPendingDelete.target_url}</span>
                      </p>
                      <p className="admin-confirmation-detail">
                        Delivery of this event to that endpoint stops immediately.
                        This cannot be undone.
                      </p>
                    </div>
                    <div className="admin-button-row">
                      <button
                        className="admin-btn-remove admin-btn-lg"
                        onClick={() => handleDeleteWebhook(webhookPendingDelete.id)}
                        disabled={deletingWebhookId !== null}
                      >
                        {deletingWebhookId !== null ? 'Deleting' : 'Yes, Delete Webhook'}
                      </button>
                      <button
                        className="admin-btn-cancel admin-btn-lg"
                        onClick={() => setWebhookPendingDelete(null)}
                        disabled={deletingWebhookId !== null}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Webhook table */}
                {webhooksLoading ? (
                  <div className="admin-no-users">Loading webhooks</div>
                ) : webhooks.length > 0 ? (
                  <div className="admin-table-wrapper">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Event Type</th>
                          <th>Target URL</th>
                          <th>Status</th>
                          <th>Timeout</th>
                          <th>Retries</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {webhooks.map(wh => (
                          <tr key={wh.id}>
                            <td><span className="webhook-event-badge">{wh.event_type}</span></td>
                            <td className="webhook-url-cell" title={wh.target_url}>{wh.target_url}</td>
                            <td>
                              <span className={`webhook-status-badge ${wh.is_active ? 'webhook-status-active' : 'webhook-status-inactive'}`}>
                                {wh.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td>{wh.timeout_seconds}s</td>
                            <td>{wh.max_retries}</td>
                            <td>
                              <div className="admin-action-buttons">
                                <button
                                  className="admin-btn-switch"
                                  onClick={() => startEditWebhook(wh)}
                                  disabled={deletingWebhookId !== null || showWebhookForm}
                                >
                                  Edit
                                </button>
                                <button
                                  className="admin-btn-remove"
                                  onClick={() => setWebhookPendingDelete(wh)}
                                  disabled={deletingWebhookId !== null || showWebhookForm}
                                >
                                  {deletingWebhookId === wh.id ? 'Deleting' : 'Delete'}
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="admin-no-users admin-no-users--mb">No webhooks configured</div>
                )}

                {!showWebhookForm ? (
                  <div className="admin-button-row">
                    <button
                      className="primary-button"
                      onClick={() => { setShowWebhookForm(true); setWebhookFormError(''); setWebhookFormSuccess(''); }}
                    >
                      Add Webhook
                    </button>
                  </div>
                ) : (
                  <div className="webhook-form-box">
                    <h3 className="webhook-form-title">
                      {editingWebhookId ? 'Edit Webhook' : 'New Webhook'}
                    </h3>

                    {webhookFormError && (
                      <div className="admin-error-alert">
                        {webhookFormError}
                      </div>
                    )}

                    <div className="webhook-form-grid">
                      <div className="webhook-form-field">
                        <label className="webhook-form-label">Event Type</label>
                        <select
                          className="webhook-form-select"
                          value={webhookForm.event_type}
                          onChange={e => setWebhookForm(f => ({ ...f, event_type: e.target.value }))}
                        >
                          {ALLOWED_EVENT_TYPES.map(et => (
                            <option key={et} value={et}>{et}</option>
                          ))}
                        </select>
                      </div>

                      <div className="webhook-form-field webhook-form-field--wide">
                        <label className="webhook-form-label">Target URL</label>
                        <input
                          className="webhook-form-input"
                          type="url"
                          placeholder="https://example.com/webhook"
                          value={webhookForm.target_url}
                          onChange={e => setWebhookForm(f => ({ ...f, target_url: e.target.value }))}
                        />
                      </div>

                      <div className="webhook-form-field">
                        <label className="webhook-form-label">
                          Secret {editingWebhookId && <span className="webhook-form-hint">(leave blank to keep current)</span>}
                        </label>
                        <input
                          className="webhook-form-input"
                          type="password"
                          placeholder={editingWebhookId ? '••••••••' : 'Signing secret'}
                          value={webhookForm.secret}
                          onChange={e => setWebhookForm(f => ({ ...f, secret: e.target.value }))}
                        />
                      </div>

                      <div className="webhook-form-field">
                        <label className="webhook-form-label">Timeout (seconds)</label>
                        <input
                          className="webhook-form-input"
                          type="number"
                          min="1"
                          max="60"
                          value={webhookForm.timeout_seconds}
                          onChange={e => setWebhookForm(f => ({ ...f, timeout_seconds: parseInt(e.target.value, 10) || 8 }))}
                        />
                      </div>

                      <div className="webhook-form-field">
                        <label className="webhook-form-label">Max Retries</label>
                        <input
                          className="webhook-form-input"
                          type="number"
                          min="0"
                          max="10"
                          value={webhookForm.max_retries}
                          onChange={e => setWebhookForm(f => ({ ...f, max_retries: parseInt(e.target.value, 10) || 0 }))}
                        />
                      </div>

                      <div className="webhook-form-field">
                        <label className="webhook-form-label">Retry Backoff Base</label>
                        <input
                          className="webhook-form-input"
                          type="number"
                          min="1"
                          max="10"
                          value={webhookForm.retry_backoff_base}
                          onChange={e => setWebhookForm(f => ({ ...f, retry_backoff_base: parseInt(e.target.value, 10) || 2 }))}
                        />
                      </div>

                      <div className="webhook-form-field">
                        <label className="admin-checkbox-label admin-checkbox-label--aligned">
                          <input
                            type="checkbox"
                            checked={webhookForm.is_active}
                            onChange={e => setWebhookForm(f => ({ ...f, is_active: e.target.checked }))}
                          />
                          Active
                        </label>
                      </div>
                    </div>

                    <div className="admin-button-row">
                      <button
                        className="primary-button"
                        onClick={handleSaveWebhook}
                        disabled={savingWebhook || !webhookForm.target_url}
                      >
                        {savingWebhook ? 'Saving' : editingWebhookId ? 'Update Webhook' : 'Create Webhook'}
                      </button>
                      <button
                        className="admin-btn-cancel"
                        onClick={cancelWebhookForm}
                        disabled={savingWebhook}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </section>
        </>
      )}
      </div>
    </PageContainer>
  );
};

export default PlatformAdmin;
